// 分类浏览和筛选功能实现

import type {
  Category,
  SearchQuery,
  SearchResult
} from '@/types/reading';

import { CategoryManager } from './categoryManager';
import { SearchService } from './searchService';
import type { StorageManager } from './storage';

export interface CategoryFilter {
  minBookCount?: number;
  maxBookCount?: number;
  favoriteOnly?: boolean;
  searchKeyword?: string;
  sortBy?: CategorySortBy;
}

export type CategorySortBy = 'name' | 'bookCount' | 'recent' | 'custom';

export interface CategoryBreadcrumb {
  id: string;
  name: string;
  url: string;
}

export interface CategoryNavigationState {
  currentCategory?: Category;
  breadcrumbs: CategoryBreadcrumb[];
  parentCategories: Category[];
  childCategories: Category[];
  siblingCategories: Category[];
}

export class CategoryBrowser {
  private categoryManager: CategoryManager;
  private searchService: SearchService;
  private storage: StorageManager;
  private navigationHistory: CategoryNavigationState[] = [];
  private readonly MAX_HISTORY_SIZE = 20;

  constructor(
    categoryManager: CategoryManager,
    searchService: SearchService,
    storage: StorageManager
  ) {
    this.categoryManager = categoryManager;
    this.searchService = searchService;
    this.storage = storage;
  }

  // ============================================================================
  // 分类搜索和筛选功能
  // ============================================================================

  async searchCategories(filter: CategoryFilter): Promise<Category[]> {
    try {
      let categories = await this.categoryManager.getAllCategories();

      // 应用筛选条件
      if (filter.minBookCount !== undefined) {
        categories = categories.filter(cat => cat.bookCount >= filter.minBookCount!);
      }

      if (filter.maxBookCount !== undefined) {
        categories = categories.filter(cat => cat.bookCount <= filter.maxBookCount!);
      }

      if (filter.favoriteOnly) {
        const favoriteCategories = await this.categoryManager.getFavoriteCategories();
        const favoriteNames = new Set(favoriteCategories.map(cat => cat.name));
        categories = categories.filter(cat => favoriteNames.has(cat.name));
      }

      if (filter.searchKeyword) {
        const keyword = filter.searchKeyword.toLowerCase();
        categories = categories.filter(cat => 
          cat.name.toLowerCase().includes(keyword) ||
          (cat.description && cat.description.toLowerCase().includes(keyword))
        );
      }

      // 排序
      categories = await this.sortCategories(categories, filter.sortBy || 'bookCount');

      return categories;

    } catch (error) {
      console.error('Search categories failed:', error);
      return [];
    }
  }

  private async sortCategories(categories: Category[], sortBy: CategorySortBy): Promise<Category[]> {
    switch (sortBy) {
      case 'name':
        return categories.sort((a, b) => a.name.localeCompare(b.name));
      
      case 'bookCount':
        return categories.sort((a, b) => b.bookCount - a.bookCount);
      
      case 'recent':
        return categories.sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0));
      
      case 'custom': {
        const sortedCategories = await this.categoryManager.getSortedCategories();
        return sortedCategories;
      }
      
      default:
        return categories;
    }
  }

  async searchBooksInCategory(
    categoryId: string, 
    searchQuery: Partial<SearchQuery>,
    page: number = 1,
    pageSize: number = 20
  ): Promise<SearchResult> {
    try {
      const category = await this.categoryManager.getCategoryById(categoryId);
      if (!category) {
        return this.createEmptyResult(page, pageSize);
      }

      // 构建完整的搜索查询
      const fullQuery: SearchQuery = {
        keyword: searchQuery.keyword || '',
        category: category.name,
        author: searchQuery.author,
        tags: searchQuery.tags,
        sortBy: searchQuery.sortBy || 'relevance',
        page,
        pageSize,
        sourceIds: searchQuery.sourceIds,
        searchScope: searchQuery.searchScope || 'all'
      };

      // 执行搜索
      const result = await this.searchService.searchBooks(fullQuery);

      // 记录分类浏览历史
      await this.recordCategoryVisit(categoryId);

      return result;

    } catch (error) {
      console.error('Search books in category failed:', error);
      return this.createEmptyResult(page, pageSize);
    }
  }

  // ============================================================================
  // 分类导航和面包屑
  // ============================================================================

  async getCategoryNavigation(categoryId?: string): Promise<CategoryNavigationState> {
    try {
      const currentCategory = categoryId ? 
        await this.categoryManager.getCategoryById(categoryId) : undefined;

      const breadcrumbs = await this.buildBreadcrumbs(currentCategory || undefined);
      const parentCategories = await this.getParentCategories(currentCategory || undefined);
      const childCategories = await this.getChildCategories(currentCategory || undefined);
      const siblingCategories = await this.getSiblingCategories(currentCategory || undefined);

      const navigationState: CategoryNavigationState = {
        currentCategory: currentCategory || undefined,
        breadcrumbs,
        parentCategories,
        childCategories,
        siblingCategories
      };

      // 添加到导航历史
      this.addToNavigationHistory(navigationState);

      return navigationState;

    } catch (error) {
      console.error('Get category navigation failed:', error);
      return {
        breadcrumbs: [],
        parentCategories: [],
        childCategories: [],
        siblingCategories: []
      };
    }
  }

  private async buildBreadcrumbs(category?: Category): Promise<CategoryBreadcrumb[]> {
    const breadcrumbs: CategoryBreadcrumb[] = [];

    // 添加根节点
    breadcrumbs.push({
      id: 'root',
      name: '所有分类',
      url: '/categories'
    });

    if (category) {
      // 添加父分类路径
      const parents = await this.getCategoryPath(category);
      parents.forEach(parent => {
        breadcrumbs.push({
          id: parent.id,
          name: parent.name,
          url: `/categories/${parent.id}`
        });
      });

      // 添加当前分类
      breadcrumbs.push({
        id: category.id,
        name: category.name,
        url: `/categories/${category.id}`
      });
    }

    return breadcrumbs;
  }

  private async getCategoryPath(category: Category): Promise<Category[]> {
    const path: Category[] = [];
    let current = category;

    // 向上遍历父分类
    while (current.parentId) {
      const parent = await this.categoryManager.getCategoryById(current.parentId);
      if (parent) {
        path.unshift(parent);
        current = parent;
      } else {
        break;
      }
    }

    return path;
  }

  private async getParentCategories(category?: Category): Promise<Category[]> {
    if (!category || !category.parentId) {
      return [];
    }

    const parent = await this.categoryManager.getCategoryById(category.parentId);
    return parent ? [parent] : [];
  }

  private async getChildCategories(category?: Category): Promise<Category[]> {
    if (!category) {
      // 返回顶级分类
      const allCategories = await this.categoryManager.getAllCategories();
      return allCategories.filter(cat => !cat.parentId);
    }

    // 返回子分类
    const allCategories = await this.categoryManager.getAllCategories();
    return allCategories.filter(cat => cat.parentId === category.id);
  }

  private async getSiblingCategories(category?: Category): Promise<Category[]> {
    if (!category) {
      return [];
    }

    const allCategories = await this.categoryManager.getAllCategories();
    return allCategories.filter(cat => 
      cat.id !== category.id && cat.parentId === category.parentId
    );
  }

  // ============================================================================
  // 分类内容一致性检查
  // ============================================================================

  async validateCategoryConsistency(): Promise<{
    isConsistent: boolean;
    issues: Array<{
      type: 'missing_books' | 'duplicate_books' | 'invalid_category' | 'empty_category';
      categoryId: string;
      categoryName: string;
      description: string;
      severity: 'low' | 'medium' | 'high';
    }>;
  }> {
    try {
      const issues: Array<{
        type: 'missing_books' | 'duplicate_books' | 'invalid_category' | 'empty_category';
        categoryId: string;
        categoryName: string;
        description: string;
        severity: 'low' | 'medium' | 'high';
      }> = [];

      const categories = await this.categoryManager.getAllCategories();

      for (const category of categories) {
        // 检查空分类
        if (category.bookCount === 0) {
          issues.push({
            type: 'empty_category',
            categoryId: category.id,
            categoryName: category.name,
            description: `分类 "${category.name}" 没有包含任何图书`,
            severity: 'low'
          });
        }

        // 检查分类图书数量一致性
        const actualBooks = await this.categoryManager.getCategoryBooks(category.id, 1, 1000);
        if (actualBooks.total !== category.bookCount) {
          issues.push({
            type: 'missing_books',
            categoryId: category.id,
            categoryName: category.name,
            description: `分类 "${category.name}" 显示有 ${category.bookCount} 本书，但实际只找到 ${actualBooks.total} 本`,
            severity: 'medium'
          });
        }

        // 检查重复图书
        const bookTitles = new Set<string>();
        const duplicates: string[] = [];
        
        actualBooks.books.forEach(book => {
          const key = `${book.title}_${book.author}`;
          if (bookTitles.has(key)) {
            duplicates.push(book.title);
          } else {
            bookTitles.add(key);
          }
        });

        if (duplicates.length > 0) {
          issues.push({
            type: 'duplicate_books',
            categoryId: category.id,
            categoryName: category.name,
            description: `分类 "${category.name}" 包含重复图书: ${duplicates.join(', ')}`,
            severity: 'medium'
          });
        }
      }

      return {
        isConsistent: issues.length === 0,
        issues
      };

    } catch (error) {
      console.error('Validate category consistency failed:', error);
      return {
        isConsistent: false,
        issues: [{
          type: 'invalid_category',
          categoryId: 'unknown',
          categoryName: 'Unknown',
          description: '分类一致性检查失败',
          severity: 'high'
        }]
      };
    }
  }

  async fixCategoryConsistency(): Promise<boolean> {
    try {
      const validation = await this.validateCategoryConsistency();
      
      if (validation.isConsistent) {
        return true;
      }

      // 修复问题
      for (const issue of validation.issues) {
        switch (issue.type) {
          case 'empty_category':
            // 可以选择删除空分类或保留
            console.warn(`Empty category found: ${issue.categoryName}`);
            break;

          case 'missing_books':
            // 重新计算分类图书数量
            await this.recalculateCategoryBookCount(issue.categoryId);
            break;

          case 'duplicate_books':
            // 去重处理
            await this.removeDuplicateBooksFromCategory(issue.categoryId);
            break;

          default:
            console.warn(`Unknown issue type: ${issue.type}`);
        }
      }

      // 刷新分类缓存
      this.categoryManager.clearCache();
      
      return true;

    } catch (error) {
      console.error('Fix category consistency failed:', error);
      return false;
    }
  }

  private async recalculateCategoryBookCount(categoryId: string): Promise<void> {
    try {
      const books = await this.categoryManager.getCategoryBooks(categoryId, 1, 10000);
      // 这里需要更新分类的bookCount，但由于分类是动态生成的，
      // 实际上需要更新底层图书数据或缓存
      console.log(`Category ${categoryId} actual book count: ${books.total}`);
    } catch (error) {
      console.error('Recalculate category book count failed:', error);
    }
  }

  private async removeDuplicateBooksFromCategory(categoryId: string): Promise<void> {
    try {
      // 这里需要实现去重逻辑，可能需要调用BookLibrary的去重功能
      console.log(`Removing duplicates from category ${categoryId}`);
    } catch (error) {
      console.error('Remove duplicate books from category failed:', error);
    }
  }

  // ============================================================================
  // 分类浏览历史和统计
  // ============================================================================

  private addToNavigationHistory(state: CategoryNavigationState): void {
    this.navigationHistory.unshift(state);
    
    if (this.navigationHistory.length > this.MAX_HISTORY_SIZE) {
      this.navigationHistory = this.navigationHistory.slice(0, this.MAX_HISTORY_SIZE);
    }
  }

  getNavigationHistory(): CategoryNavigationState[] {
    return [...this.navigationHistory];
  }

  async getRecentlyVisitedCategories(limit: number = 10): Promise<Category[]> {
    try {
      const visitHistory = await this.storage.getUserData('category_visit_history') || [];
      const recentCategoryIds = visitHistory
        .sort((a: any, b: any) => b.timestamp - a.timestamp)
        .slice(0, limit)
        .map((visit: any) => visit.categoryId);

      const categories: Category[] = [];
      
      for (const categoryId of recentCategoryIds) {
        const category = await this.categoryManager.getCategoryById(categoryId);
        if (category) {
          categories.push(category);
        }
      }

      return categories;

    } catch (error) {
      console.error('Get recently visited categories failed:', error);
      return [];
    }
  }

  private async recordCategoryVisit(categoryId: string): Promise<void> {
    try {
      const visitHistory = await this.storage.getUserData('category_visit_history') || [];
      
      // 移除已存在的记录
      const filteredHistory = visitHistory.filter((visit: any) => visit.categoryId !== categoryId);
      
      // 添加新记录
      filteredHistory.unshift({
        categoryId,
        timestamp: Date.now()
      });

      // 限制历史记录数量
      const limitedHistory = filteredHistory.slice(0, 50);
      
      await this.storage.saveUserData('category_visit_history', limitedHistory);

    } catch (error) {
      console.error('Record category visit failed:', error);
    }
  }

  async getCategoryBrowsingStats(): Promise<{
    totalVisits: number;
    uniqueCategories: number;
    mostVisitedCategories: Array<{ category: Category; visitCount: number }>;
    recentActivity: Array<{ category: Category; lastVisit: Date }>;
  }> {
    try {
      const visitHistory = await this.storage.getUserData('category_visit_history') || [];
      
      // 统计访问次数
      const visitCounts = new Map<string, number>();
      visitHistory.forEach((visit: any) => {
        const count = visitCounts.get(visit.categoryId) || 0;
        visitCounts.set(visit.categoryId, count + 1);
      });

      // 获取最常访问的分类
      const mostVisitedCategories: Array<{ category: Category; visitCount: number }> = [];
      
      for (const [categoryId, count] of visitCounts.entries()) {
        const category = await this.categoryManager.getCategoryById(categoryId);
        if (category) {
          mostVisitedCategories.push({ category, visitCount: count });
        }
      }

      mostVisitedCategories.sort((a, b) => b.visitCount - a.visitCount);

      // 获取最近活动
      const recentActivity: Array<{ category: Category; lastVisit: Date }> = [];
      const recentVisits = visitHistory
        .sort((a: any, b: any) => b.timestamp - a.timestamp)
        .slice(0, 10);

      for (const visit of recentVisits) {
        const category = await this.categoryManager.getCategoryById(visit.categoryId);
        if (category) {
          recentActivity.push({
            category,
            lastVisit: new Date(visit.timestamp)
          });
        }
      }

      return {
        totalVisits: visitHistory.length,
        uniqueCategories: visitCounts.size,
        mostVisitedCategories: mostVisitedCategories.slice(0, 10),
        recentActivity
      };

    } catch (error) {
      console.error('Get category browsing stats failed:', error);
      return {
        totalVisits: 0,
        uniqueCategories: 0,
        mostVisitedCategories: [],
        recentActivity: []
      };
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private createEmptyResult(page: number, pageSize: number): SearchResult {
    return {
      books: [],
      total: 0,
      page,
      pageSize,
      suggestions: [],
      searchTime: Date.now()
    };
  }

  clearNavigationHistory(): void {
    this.navigationHistory = [];
  }

  async clearBrowsingHistory(): Promise<void> {
    try {
      await this.storage.saveUserData('category_visit_history', []);
    } catch (error) {
      console.error('Clear browsing history failed:', error);
    }
  }
}